import React, { useState, useRef, useEffect } from 'react';
import { X, GraduationCap } from 'lucide-react';

// Education qualification options
const EDUCATION_OPTIONS = [
  '10th',
  '12th', 
  'Graduate',
  'Post Graduate'
];

/**
 * Education Selector with Predefined Options and Autocomplete
 * @param {Array} selectedEducation - Array of currently selected education qualifications
 * @param {Function} onChange - Callback when education changes
 * @param {Number} maxEducation - Maximum number of education entries allowed (default: 5)
 * @param {Boolean} required - Whether education is required
 * @param {String} placeholder - Placeholder text
 */
const EducationSelector = ({ 
  selectedEducation = [], 
  onChange, 
  maxEducation = 5,
  required = false,
  placeholder = 'Select your education qualification...'
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter options based on input and exclude already selected
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = EDUCATION_OPTIONS.filter(option =>
        option.toLowerCase().includes(inputValue.toLowerCase()) &&
        !selectedEducation.includes(option)
      );
      setFilteredOptions(filtered);
    } else {
      const available = EDUCATION_OPTIONS.filter(option => !selectedEducation.includes(option));
      setFilteredOptions(available);
    }
  }, [inputValue, selectedEducation]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addEducation = (education) => {
    const educationToAdd = education || inputValue.trim();
    
    if (!educationToAdd) return;
    
    if (selectedEducation.length >= maxEducation) {
      alert(`You can only add up to ${maxEducation} education qualifications`);
      return;
    }
    
    if (selectedEducation.includes(educationToAdd)) {
      setInputValue('');
      setShowSuggestions(false);
      return;
    }
    
    onChange([...selectedEducation, educationToAdd]);
    setInputValue('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const removeEducation = (educationToRemove) => {
    onChange(selectedEducation.filter(e => e !== educationToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addEducation();
    } else if (e.key === 'Backspace' && !inputValue && selectedEducation.length > 0) {
      removeEducation(selectedEducation[selectedEducation.length - 1]);
    }
  };

  return (
    <div className="w-full">
      {/* Selected Education Tags */}
      {selectedEducation.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selectedEducation.map((education, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-full text-sm font-medium"
            >
              <GraduationCap size={14} />
              {education}
              <button
                type="button"
                onClick={() => removeEducation(education)}
                className="hover:bg-green-700 rounded-full p-0.5 transition-colors"
                aria-label={`Remove ${education}`}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input Field */}
      <div className="relative">
        <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          required={required && selectedEducation.length === 0}
          autoComplete="off"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
        />

        {/* Autocomplete Dropdown */}
        {showSuggestions && filteredOptions.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
          >
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
              <p className="text-xs text-gray-600 font-medium">
                Select Education Level
              </p>
            </div>
            {filteredOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => addEducation(option)}
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 text-sm transition-colors flex items-center gap-2"
              >
                <GraduationCap size={16} className="text-gray-400" />
                <span>{option}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Helper Text */}
      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
        <span>Select from options or type custom qualification</span>
        <span className={selectedEducation.length >= maxEducation ? 'text-red-600 font-medium' : ''}>
          {selectedEducation.length} / {maxEducation} qualifications
        </span>
      </div>
    </div>
  );
};

export default EducationSelector;
